import axios from '../axiosConfig';

export interface FiltersResponse {
	productFamilies: string[];
	productLines: string[];
	users: Array<{ name: string; emailAddress: string; avatarUri?: string | null }>;
	pcls: string[];
}

async function safeGet<T = any>(url: string, params?: Record<string, any>): Promise<T | null> {
	try {
		const { data } = await axios.get<T>(url, { params });
		return data;
	} catch (_e) {
		return null;
	}
}

export async function fetchAllFilters(
    productFamily?: string | null,
    productLine?: string | null,
    users?: string[] | null,
    pcls?: string[] | null,
    keyword?: string | null,
    includeUsers?: boolean,
): Promise<FiltersResponse> {
	// Build endpoint-specific params to match BE expectations
	const paramsFamilies: Record<string, any> = {};
	if (productLine) paramsFamilies.ProductLine = productLine;
	if (keyword) paramsFamilies.keyword = keyword;

	const paramsLines: Record<string, any> = {};
	if (productFamily) paramsLines.ProductFamily = productFamily; // IMPORTANT: uppercase key
	if (keyword) paramsLines.keyword = keyword;

	const paramsPcls: Record<string, any> = {};
	if (productFamily) paramsPcls.ProductFamily = productFamily;
	if (productLine) paramsPcls.ProductLine = productLine;
	if (pcls && pcls.length) paramsPcls.pcls = pcls.join(',');
	if (users && users.length) paramsPcls.users = users.join(',');
	if (keyword) paramsPcls.keyword = keyword;

	const paramsUsers: Record<string, any> = {};
	if (productFamily) paramsUsers.ProductFamily = productFamily;
	if (productLine) paramsUsers.ProductLine = productLine;
	if (keyword) paramsUsers.keyword = keyword;

	const shouldFetchUsers = includeUsers !== false;

    const [familiesRes, linesRes, pclsRes, usersRes] = await Promise.all([
		safeGet<any>(`/ProductFamilies`, paramsFamilies),
		safeGet<any>(`/ProductLines`, paramsLines),
		safeGet<any>(`/Pcls`, paramsPcls),
		shouldFetchUsers ? safeGet<any>(`/Users`, paramsUsers) : Promise.resolve(null),
	]);

	const isArray = (v: any) => Array.isArray(v);

	const usersList: Array<{ name: string; emailAddress: string; avatarUri?: string | null }> = Array.isArray((usersRes as any)?.results)
		? ((usersRes as any).results as Array<{ id?: string; userId?: string; name?: string; emailAddress?: string; avatarUri?: string | null }>)
			.map(u => ({
				name: (u?.name || '').trim(),
				emailAddress: (u?.emailAddress || '').trim(),
				avatarUri: u?.avatarUri ?? null,
			}))
			.filter(u => Boolean(u.name) && Boolean(u.emailAddress))
		: [];

	return {
		productFamilies: isArray(familiesRes) ? (familiesRes as string[]) : [],
		productLines: isArray(linesRes) ? (linesRes as string[]) : [],
		pcls: isArray(pclsRes) ? (pclsRes as string[]) : [],
		users: usersList,
	};
}

export async function fetchProductFamilies(businessUnit?: string | null): Promise<string[]> {
	const params = businessUnit ? { businessUnit } : undefined;
	const res = await safeGet<any>(`/ProductFamilies`, params);
	if (!Array.isArray(res)) return [];
	return res as string[];
}

export async function fetchProductLines(productFamily?: string | null): Promise<string[]> {
	const params = productFamily ? { ProductFamily: productFamily } : undefined; // IMPORTANT: uppercase key
	const res = await safeGet<any>(`/ProductLines`, params);
	if (!Array.isArray(res)) return [];
	return res as string[];
}

export async function fetchRelatedPCLs(productFamily?: string | null, productLine?: string | null): Promise<string[]> {
	const params: Record<string, any> = {};
	if (productFamily) params.productFamily = productFamily;
	if (productLine) params.productLine = productLine;
	const res = await safeGet<any>(`/Pcls`, params);
	if (!Array.isArray(res)) return [];
	return res as string[];
}

export async function fetchUsers(productFamily?: string | null, productLine?: string | null): Promise<string[]> {
	const res = await safeGet<any>(`/Users`);
	const list: string[] = Array.isArray(res?.results)
		? res.results.map((u: any) => (u?.name || '').trim()).filter(Boolean)
		: [];
	return list;
}

export async function fetchBusinessUnits(): Promise<string[]> {
	const res = await safeGet<any>(`/BusinessUnit`);
	if (!Array.isArray(res)) return [];
	return res as string[];
}


